import { useState } from 'react'
import { Link } from 'react-router-dom'
import { download, query } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { EmployeeSchedule, EmployeeScheduleRow } from '../../api/types'
import { useAuth } from '../../auth/AuthContext'
import { P } from '../../auth/permissions'
import {
  Empty,
  ErrorBanner,
  Loading,
  Money,
  Panel,
  formatDate,
  isoDate,
  money,
} from '../../components/ui'

/**
 * Who is holding office cash, since when, and does it agree with the ledger (BUILD_PLAN §3.2).
 *
 * Custody is settled with receipts, a business loan is recovered from salary over months, and a
 * salary advance comes off the same month's salary. Three balances, three mechanics — a single
 * "employee balance" cannot say which of the three is overdue, which is the only question anyone
 * asks of this page.
 */
export function EmployeeSchedulePage() {
  const { can } = useAuth()
  const [asOf, setAsOf] = useState(isoDate(new Date()))

  const report = useQuery<EmployeeSchedule>(`/reports/employee-schedule${query({ asOf })}`, [asOf])

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>As at</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </div>
        <div className="spacer" />
        {can(P.reports.export) && (
          <button
            onClick={() =>
              void download(
                `/reports/employee-schedule/excel${query({ asOf })}`,
                `employee-schedule-${asOf}.xlsx`,
              )
            }
          >
            Export to Excel
          </button>
        )}
      </div>

      <ErrorBanner error={report.error} />
      {report.loading && <Loading what="Building the schedule" />}

      {report.data && (
        <>
          {!report.data.tiesOut && (
            <div className="banner warn">
              <div>
                <strong>One or more accounts disagree with the documents behind them.</strong> An
                entry has moved a custody or loan account without naming what it settles — usually a
                hand-written journal entry, or an opening balance brought in before the schedule
                existed. The difference is shown against the employee below.
              </div>
            </div>
          )}

          <div className="grid cols-4" style={{ marginBottom: 16 }}>
            <div className="metric">
              <div className="label">Custody outstanding</div>
              <div className="value">{money(report.data.totalCustody)}</div>
              <div className="hint">Office cash in employees' hands, awaiting receipts.</div>
            </div>

            <div className="metric">
              <div className="label">Business loans</div>
              <div className="value">{money(report.data.totalLoans)}</div>
              <div className="hint">Recovered from salary on a schedule.</div>
            </div>

            <div className="metric">
              <div className="label">Salary advances</div>
              <div className="value">{money(report.data.totalAdvances)}</div>
              <div className="hint">Off the same month's salary. No document to settle.</div>
            </div>

            <div className="metric">
              <div className="label">Total owed to the office</div>
              <div className="value">{money(report.data.totalOwed)}</div>
              <div className="hint">All three balances, across every employee.</div>
            </div>
          </div>

          {report.data.employees.length === 0 && (
            <Panel>
              <Empty>No employee is holding custody or carrying a loan as at this date.</Empty>
            </Panel>
          )}

          {report.data.employees.map((employee) => (
            <Employee key={employee.partyId} employee={employee} />
          ))}
        </>
      )}
    </>
  )
}

/**
 * One employee's own panel rather than a row in a shared table. The conversation is always with one
 * person about their own items, and that is how the sheet gets used — printed, taken to a desk,
 * signed when the cash comes back.
 */
function Employee({ employee }: { employee: EmployeeScheduleRow }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <Panel title={`${employee.partyCode} · ${employee.partyName}`} flush>
        <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th style={{ width: 90 }}>Type</th>
              <th style={{ width: 140 }}>Document</th>
              <th style={{ width: 100 }}>Issued</th>
              <th style={{ width: 90 }} className="num">
                Days
              </th>
              <th className="num">Amount</th>
              <th className="num">Settled</th>
              <th className="num">Outstanding</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {employee.items.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <Empty>No open documents — the balances below come from the ledger alone.</Empty>
                </td>
              </tr>
            )}

            {employee.items.map((item) => (
              <tr key={item.openItemId}>
                <td>
                  <span className={item.docType === 'LoanIssued' ? 'tag' : 'tag warn'}>
                    {item.docType === 'LoanIssued' ? 'Loan' : 'Custody'}
                  </span>
                </td>
                <td className="code">
                  {/* Custody and loans have no paper of their own — the entry that paid the money
                      out is the document, so the reference is only useful if you can open it. */}
                  {item.journalEntryId ? (
                    <Link to={`/journal/${item.journalEntryId}`}>{item.docNo}</Link>
                  ) : (
                    item.docNo
                  )}
                </td>
                <td>{formatDate(item.date)}</td>
                <td className="num">
                  {/* The number anyone actually chases: custody outstanding for months is either
                      lost paperwork or money that is not coming back. */}
                  <span className={item.daysOutstanding > 90 ? 'negative' : undefined}>
                    {item.daysOutstanding}
                  </span>
                </td>
                <td className="num">
                  <Money value={item.amount} />
                </td>
                <td className="num">
                  <Money value={item.settled} blankZero />
                </td>
                <td className="num strong">
                  <Money value={item.outstanding} />
                </td>
                <td className="muted">
                  {item.instalmentCount
                    ? `${item.instalmentCount} × ${money(item.instalmentAmount ?? 0)}`
                    : (item.description ?? '')}
                  {item.currency !== 'EGP' && ` · ${item.currency}`}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={6}>Custody</td>
              <td className="num">
                <Money value={employee.custodyOutstanding} blankZero />
              </td>
              <td className="negative">{varianceNote(employee.custodyVariance)}</td>
            </tr>
            <tr>
              <td colSpan={6}>Business loans</td>
              <td className="num">
                <Money value={employee.loanOutstanding} blankZero />
              </td>
              <td className="negative">{varianceNote(employee.loanVariance)}</td>
            </tr>
            <tr>
              <td colSpan={6}>Salary advance</td>
              <td className="num">
                <Money value={employee.advanceBalance} blankZero />
              </td>
              <td className="muted">Ledger balance — advances raise no document.</td>
            </tr>
            <tr>
              <td colSpan={6} className="strong">
                Total owed
              </td>
              <td className="num strong">
                <Money value={employee.totalOwed} />
              </td>
              <td />
            </tr>
          </tfoot>
          </table>
        </div>
      </Panel>
    </div>
  )
}

/** Shown only when the ledger and the documents disagree; blank is the normal case. */
function varianceNote(variance: number): string {
  return Math.abs(variance) <= 0.01 ? '' : `Ledger differs by ${money(variance)}`
}
